import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import { FaVideo, FaBook, FaQuestionCircle, FaFileAlt, FaChevronRight, FaArrowRight } from 'react-icons/fa';
import { useMobileDetector } from '../utils/deviceDetection';

// Desktop version of training card
const TrainingCard = ({ icon: Icon, title, description }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-6 rounded-lg border border-gray-700 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
    <div className="text-blue-400 text-3xl mb-4">
      <Icon />
    </div>
    <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
    <p className="text-blue-100">{description}</p>
  </div>
);

// Mobile version of training resource with horizontal layout
const MobileTrainingResource = ({ icon: Icon, title, description, onClick }) => (
  <div className="bg-gradient-to-br from-surface to-gray-800 p-4 rounded-lg flex items-center justify-between border border-gray-700 shadow-lg mb-3">
    <div className="flex items-center">
      <div className="text-blue-400 text-2xl mr-4">
        <Icon />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
        <p className="text-blue-100 text-sm">{description.substring(0, 40)}...</p>
      </div>
    </div>
    <FaChevronRight className="text-blue-400" />
  </div>
);

// Mobile resource detail view
const ResourceDetailView = ({ resource, onBack }) => {
  const Icon = resource.icon;
  
  return (
    <div className="bg-gradient-to-br from-surface to-gray-800 p-5 rounded-lg border border-gray-700 shadow-lg mb-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <div className="text-blue-400 text-2xl mr-3">
            <Icon />
          </div>
          <h3 className="text-xl font-semibold text-white">{resource.title}</h3>
        </div>
        <button 
          onClick={onBack}
          className="text-blue-400 hover:text-blue-300"
        >
          Back
        </button>
      </div>
      
      <p className="text-blue-100 mb-4">{resource.description}</p>
      
      <div className="space-y-4">
        <div className="border-t border-gray-700 pt-4">
          <h4 className="font-semibold text-white mb-2">Getting Started</h4>
          <ul className="text-blue-100 ml-4 space-y-2">
            <li className="flex items-center">
              <FaArrowRight className="text-blue-400 mr-2 text-xs" />
              <span>Introduction to {resource.title}</span>
            </li>
            <li className="flex items-center">
              <FaArrowRight className="text-blue-400 mr-2 text-xs" />
              <span>Basic functionality</span>
            </li>
            <li className="flex items-center">
              <FaArrowRight className="text-blue-400 mr-2 text-xs" />
              <span>Common use cases</span>
            </li>
          </ul>
        </div>
        <div className="border-t border-gray-700 pt-4">
          <h4 className="font-semibold text-white mb-2">Advanced Topics</h4>
          <ul className="text-blue-100 ml-4 space-y-2">
            <li className="flex items-center">
              <FaArrowRight className="text-blue-400 mr-2 text-xs" />
              <span>Pro tips and tricks</span>
            </li>
            <li className="flex items-center">
              <FaArrowRight className="text-blue-400 mr-2 text-xs" />
              <span>Troubleshooting</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const Training = () => {
  const isMobile = useMobileDetector();
  const [selectedResource, setSelectedResource] = useState(null);
  
  const resources = [
    {
      icon: FaVideo,
      title: "Video Tutorials",
      description: "Step-by-step video guides showing you how to use every feature of AUTOLAWN effectively."
    },
    {
      icon: FaBook,
      title: "Documentation",
      description: "Comprehensive documentation covering all aspects of the platform."
    },
    {
      icon: FaQuestionCircle,
      title: "Interactive Guides",
      description: "Interactive walkthroughs to help you learn the platform hands-on."
    },
    {
      icon: FaFileAlt,
      title: "Best Practices",
      description: "Learn industry best practices and how to optimize your lawn care business."
    }
  ];

  // Handler for mobile resource selection
  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
  };

  return (
    <div className="bg-gradient-to-b from-gray-900 to-background text-text min-h-screen">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-12 md:mb-16 relative">
          <div className="absolute inset-0 blur-[100px] bg-gradient-to-r from-blue-500/30 to-purple-500/30 transform rotate-12"></div>
          <h1 className="relative text-4xl md:text-5xl font-bold mb-4 md:mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Training Resources
          </h1>
          <p className="text-lg md:text-xl text-blue-100 max-w-2xl mx-auto relative">
            Everything you need to get the most out of AUTOLAWN
          </p>
        </div>

        {isMobile ? (
          // Mobile: List with detail view
          selectedResource ? (
            <ResourceDetailView 
              resource={selectedResource} 
              onBack={() => setSelectedResource(null)} 
            />
          ) : (
            <div className="space-y-2">
              {resources.map((resource, index) => (
                <MobileTrainingResource 
                  key={index} 
                  {...resource} 
                  onClick={() => handleResourceClick(resource)}
                />
              ))}
            </div>
          )
        ) : (
          // Desktop: Grid layout
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {resources.map((resource, index) => (
              <TrainingCard key={index} {...resource} />
            ))}
          </div>
        )}

        <div className="text-center mt-8 md:mt-12">
          <p className="text-gray-300 mb-6">
            Need additional help? Contact our support team at{' '}
            <a href="mailto:support@autolawn.app" className="text-blue-400 hover:text-blue-300">
              support@autolawn.app
            </a>
          </p>
          
          {isMobile && (
            <a 
              href="tel:+17249143764" 
              className="block w-full py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded-lg text-center shadow-lg"
            >
              Call Support
            </a>
          )}
        </div>
      </main>
    </div>
  );
};

export default Training; 
